import * as d3 from "d3"
import { clamp, max, min } from "lodash"
import { useCallback, useState } from "react"
import { useIsMaxSm, useIsDesktopVersion } from "../../hooks/mediaHooks"
import { FaceBad, FaceBest, FaceGood, FaceNeutral, FaceWorst } from "../../images/xd/icons"
import { fontVariants, theme } from "../../styles/stitches.config"
import { formatCurrencyPerEnergy } from "../../utils/format"
import Loader from "../loader/Loader"
import ChartTooltipCanvas from "./ChartTooltipCanvas"
import { useAxisRenderers, useChartPadding, useChartRange, useWorstToBestPriceLevels } from "./hooks"
import { withResponsiveContainer } from "./withResponsiveContainer"

interface Props {
  width: number
  height: number
  prices: { priceCzk: number; date: Date }[]
  loading?: boolean
  currentValueIndex?: number
}

const CHART_UNIT = "Kč/kWh"

const TOOLTIP_R = 29
const TOOLTIP_MARGIN = 15
const TOOLTIP_WIDTH = 6 * TOOLTIP_R
const TOOLTIP_HEIGHT = 2 * TOOLTIP_R
const FONT_SIZE = 10
const FACE_SIZE = 48
const LOADER_SIZE = 60

const DailyPriceLineChart = ({ width, height, prices, currentValueIndex, loading }: Props) => {
  const [cursorHour, setCursorHour] = useState<number | null>(null)

  const currentPrice =
    currentValueIndex !== undefined && prices[currentValueIndex] ? prices[currentValueIndex]?.priceCzk : null

  const cursorPrice =
    cursorHour !== null && cursorHour < prices.length && prices[cursorHour] ? prices[cursorHour]?.priceCzk : null

  const cursorDate =
    cursorHour !== null && cursorHour < prices.length && prices[cursorHour] ? prices[cursorHour]?.date : null

  const minPrice = min(prices.map((d) => d.priceCzk)) || 0
  const maxPrice = max(prices.map((d) => d.priceCzk)) || 0

  const [xPadding, yPadding] = useChartPadding()
  const isDesktop = useIsDesktopVersion()
  const isSmall = useIsMaxSm()
  const [rangeX, rangeY] = useChartRange(width, height)

  const domainX = [0, prices.length - 1] as const
  const domainY = [min([minPrice, 0]) as number, maxPrice] as const

  const scaleX = d3.scaleLinear().domain(domainX).range(rangeX).clamp(true)
  const scaleY = d3.scaleLinear().domain(domainY).range(rangeY)

  const { renderZeroHorizontalLine, renderHorizontalLines, renderHorizontalLineUnitLabel } = useAxisRenderers(
    width,
    [scaleY],
    CHART_UNIT
  )

  const line = d3
    .line<{ priceCzk: number; date: Date }>()
    .x((_, i) => scaleX(i))
    .y((d) => scaleY(d.priceCzk))
    .curve(d3.curveStepAfter)(prices)

  const gradientPercentY1 = Math.round((scaleY(minPrice) / height) * 100)
  const gradientPercentY2 = Math.round((scaleY(maxPrice) / height) * 100)

  const ticksX = scaleX.ticks(isDesktop ? prices.length - 1 : isSmall ? prices.length / 4 : prices.length / 3)
  const ticksY = scaleY.ticks(8)

  const triangle = d3.symbol(d3.symbolTriangle, 60)()

  const flipBubble = cursorPrice === null ? false : scaleY(cursorPrice ?? 0) < 2 * TOOLTIP_R + TOOLTIP_MARGIN

  const pointerTransform =
    cursorPrice !== null && cursorHour !== null
      ? flipBubble
        ? `translate(${scaleX(cursorHour)} ${scaleY(cursorPrice ?? 0) + TOOLTIP_MARGIN + TOOLTIP_R}) scale(1 -1)`
        : `translate(${scaleX(cursorHour)} ${scaleY(cursorPrice ?? 0) - TOOLTIP_MARGIN - TOOLTIP_R})`
      : undefined

  const tooltipTransform =
    cursorPrice !== null && cursorHour !== null
      ? flipBubble
        ? `translate(${clamp(
            scaleX(cursorHour) - 2 * TOOLTIP_R,
            scaleX(0),
            width - TOOLTIP_WIDTH + 0.75 * TOOLTIP_R
          )} ${scaleY(cursorPrice ?? 0) + TOOLTIP_MARGIN + TOOLTIP_R}) scale(1 -1)`
        : `translate(${clamp(
            scaleX(cursorHour) - 2 * TOOLTIP_R,
            scaleX(0),
            width - TOOLTIP_WIDTH + 0.75 * TOOLTIP_R
          )} ${scaleY(cursorPrice ?? 0) - TOOLTIP_MARGIN - TOOLTIP_R})`
      : undefined
  const faceTransform = flipBubble
    ? `translate(${-TOOLTIP_R + 5} ${TOOLTIP_R - 5}) scale(1 -1)`
    : `translate(${-TOOLTIP_R + 5} ${-TOOLTIP_R + 5})`

  const textTransform = (marginY = 7) =>
    flipBubble ? `translate(${TOOLTIP_R + 5} ${marginY}) scale(1 -1)` : `translate(${TOOLTIP_R + 5} ${-marginY})`

  const faceState = useWorstToBestPriceLevels(minPrice, maxPrice, cursorPrice)

  const renderFace = useCallback(() => {
    switch (faceState) {
      case "BEST":
        return <FaceBest width={FACE_SIZE} height={FACE_SIZE} />
      case "GOOD":
        return <FaceGood width={FACE_SIZE} height={FACE_SIZE} />
      case "NEUTRAL":
        return <FaceNeutral width={FACE_SIZE} height={FACE_SIZE} />
      case "BAD":
        return <FaceBad width={FACE_SIZE} height={FACE_SIZE} />
      default:
        return <FaceWorst width={FACE_SIZE} height={FACE_SIZE} />
    }
  }, [faceState])

  const onChangeTooltip = useCallback(
    (localX: number | null) => {
      // preserve tooltip when touch ends or mouse is out
      if (localX == null) {
        return
      }
      //ensuring the final value will not be out of the chart
      const currentValue = Math.max(0, Math.round(scaleX.invert(localX)))
      setCursorHour(currentValue)
    },
    [scaleX]
  )

  return (
    <svg width={width} height={height} style={{ touchAction: "pan-y" }}>
      <defs>
        <linearGradient
          id="gradient"
          x1="0%"
          y1={`${gradientPercentY1}%`}
          x2="0%"
          y2={`${gradientPercentY2}%`}
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={theme.colors.successes.value} />
          <stop offset="25%" stopColor={theme.colors.green.value} />
          <stop offset="50%" stopColor={theme.colors.secondary.value} />
          <stop offset="75%" stopColor={theme.colors.warning.value} />
          <stop offset="100%" stopColor={theme.colors.errors.value} />
        </linearGradient>

        <filter id="shadow">
          <feDropShadow dx="0" dy="0" stdDeviation="6" floodColor="#00000029" />
        </filter>

        <filter id="grayscale">
          <feColorMatrix type="saturate" values="0.10" />
        </filter>
      </defs>
      <g filter={loading ? "url(#grayscale)" : undefined}>
        {renderZeroHorizontalLine(theme.colors.lines.value)}
        {renderHorizontalLines()}
        {renderHorizontalLineUnitLabel()}
        {ticksY.map((tY, index) => (
          <text
            key={index}
            x={xPadding}
            y={scaleY(tY)}
            style={{ ...fontVariants.textsSmall, fontSize: FONT_SIZE }}
            fill={theme.colors.textsDisabeled.value}
            textAnchor="middle"
          >
            {tY}
          </text>
        ))}
        <path
          d={`${line}`}
          stroke={loading ? "#FFE70A" : "url(#gradient)"}
          strokeWidth={isDesktop ? 5 : 3}
          fill="none"
        />

        {currentValueIndex !== undefined && currentPrice !== null && (
          <>
            <line
              x1={scaleX(currentValueIndex)}
              y1={scaleY(0)}
              x2={scaleX(currentValueIndex)}
              y2={scaleY(currentPrice ?? 0)}
              stroke={theme.colors.textsAlt.value}
              strokeDasharray="2,2"
            />
            <circle
              cx={scaleX(currentValueIndex)}
              cy={scaleY(currentPrice ?? 0)}
              r="6"
              fill={loading ? "#FFE70A" : "url(#gradient)"}
              stroke={theme.colors.white.value}
              strokeWidth={2}
            />
          </>
        )}
        {cursorHour !== null && cursorPrice !== null && (
          <>
            <line
              x1={scaleX(cursorHour)}
              y1={scaleY(0)}
              x2={scaleX(cursorHour)}
              y2={scaleY(cursorPrice ?? 0)}
              stroke={theme.colors.primary.value}
              strokeDasharray="2,2"
            />
            <circle
              cx={scaleX(cursorHour)}
              cy={scaleY(cursorPrice ?? 0)}
              r="6"
              fill={loading ? "#FFE70A" : "url(#gradient)"}
              stroke={theme.colors.white.value}
              strokeWidth={2}
            />
          </>
        )}
        {ticksX.map((tX, index) => (
          <text
            key={index}
            x={scaleX(tX)}
            y={height - (yPadding - 25)}
            style={{
              ...fontVariants.textsSmall,
              fontSize: FONT_SIZE,
              fontWeight: tX === currentValueIndex ? 600 : 400,
            }}
            fill={
              tX === cursorHour
                ? theme.colors.primary.value
                : tX === currentValueIndex
                ? theme.colors.textsAlt.value
                : theme.colors.textsDisabeled.value
            }
            textAnchor="middle"
          >
            {prices[tX]?.date.getHours()}:00{" "}
          </text>
        ))}
        {currentValueIndex !== undefined && (
          <circle cx={scaleX(currentValueIndex)} cy={scaleY(0)} r="4" fill={theme.colors.textsAlt.value} />
        )}
        {cursorHour !== null && (
          <circle cx={scaleX(cursorHour)} cy={scaleY(0)} r="4" fill={theme.colors.primary.value} />
        )}

        {cursorHour !== null && cursorPrice !== null && (
          <g filter="url(#shadow)">
            <g transform={pointerTransform}>
              <path
                d={`${triangle}`}
                fill={theme.colors.white.value}
                transform={`translate(0 ${TOOLTIP_R})rotate(60)`}
              />
            </g>
            <g transform={tooltipTransform}>
              <rect
                y={-TOOLTIP_R}
                x={-TOOLTIP_R}
                rx={TOOLTIP_R}
                height={TOOLTIP_HEIGHT}
                width={TOOLTIP_WIDTH}
                fill={theme.colors.white.value}
              />
              <g transform={faceTransform}>{renderFace()}</g>
              <g transform={textTransform()}>
                <text style={{ ...fontVariants.headerH5, fontSize: 10 }} fill={theme.colors.primary.value}>
                  {`CENA V ${cursorDate?.getHours()}:00`}
                </text>
              </g>
              <g transform={textTransform(-TOOLTIP_R / 2)}>
                <text style={{ ...fontVariants.textsLarge, fontSize: 14 }} fill={theme.colors.textsAlt.value}>
                  {formatCurrencyPerEnergy(cursorPrice ?? 0)}
                </text>
              </g>
            </g>
          </g>
        )}

        <ChartTooltipCanvas width={width} height={height} onChangeX={onChangeTooltip} />
      </g>
      {loading && (
        <foreignObject
          x={width / 2 - LOADER_SIZE / 2}
          y={height / 2 - LOADER_SIZE / 2}
          width={LOADER_SIZE}
          height={LOADER_SIZE}
        >
          <Loader />
        </foreignObject>
      )}
    </svg>
  )
}

const DailyPriceLineChartWithResponsiveContainer = withResponsiveContainer(DailyPriceLineChart)

export default DailyPriceLineChartWithResponsiveContainer
